import { ArrowDown, ArrowUpRight } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../i18n'

const copy = {
  ru: {
    navSystem: 'Главная',
    navLogic: 'Как работает',
    enter: 'Открыть сервис',
    label: 'Подбор и проверка стеков',
    headline: 'НАЙДИ СВОЮ ФОРМУЛУ ЭНЕРГИИ И ФОКУСА',
    lead: 'Подберите персональную комбинацию БАДов для концентрации, энергии, спокойствия или сна. Biohack AI проверит совместимость компонентов, простыми словами объяснит, как они работают, и поможет использовать ваш потенциал по максимуму.',
    primary: 'Собрать стек',
    secondary: 'Как это работает',
    research: 'Источники и уровень данных рядом',
    profiles: 'веществ в базе',
    relations: 'проверенных пар',
    stacks: 'исследовательских троек',
    sectionLabel: 'BG-02 / Как работает подбор',
    sectionTitleA: 'Не просто список.',
    sectionTitleB: 'Понятно, зачем каждый компонент.',
    sectionLead: 'Сервис сопоставляет вашу цель с данными о веществах и проверяет все пары внутри каждого варианта. Если данных недостаточно, это видно сразу.',
    steps: [
      ['Цель', 'Напишите, чего хотите добиться и чего важно избежать.'],
      ['Подбор', 'Мы найдём вещества, связанные с нужными вам эффектами.'],
      ['Проверка', 'Проверим, как вещества сочетаются между собой, и предупредим о возможных рисках.'],
      ['Результат', 'Сравните предложенные сочетания и выберите наиболее подходящее для себя.'],
    ],
    closingLabel: 'BG-03 / Начать подбор',
    closingTitle: 'НАЙДИТЕ КОМБИНАЦИЮ ПОД СВОЮ ЦЕЛЬ',
    closingCopy: 'Укажите желаемый результат и сравните подходящие комбинации с разбором совместимости и рисков.',
    open: 'Начать подбор',
    disclaimer: 'Для обучения и исследований. Не является медицинской рекомендацией.',
    systemRail: ['BIOHACK AI / БАЗА ЗНАНИЙ', '97 ВЕЩЕСТВ / 29 ПАР / 39 ТРОЕК', 'FINAL PRODUCTION / 18.08.2026'],
    objectLabel: ['СХЕМА 01 / НЕЙРОБИОЛОГИЧЕСКАЯ МОДЕЛЬ', 'СИГНАЛЬНЫЕ ПУТИ / СХЕМА'],
    footerMeta: 'МИНСК / БЕЛАРУСЬ',
  },
  en: {
    navSystem: 'Home',
    navLogic: 'How it works',
    enter: 'Open the app',
    label: 'Stack selection and interaction checks',
    headline: 'THE BEST VERSION OF YOU STARTS WITH BIOHACK AI',
    lead: 'Find a personalized supplement combination for focus, energy, calm, or sleep. Biohack AI checks how the ingredients work together, explains each one in plain language, and helps you perform at your best.',
    primary: 'Build a stack',
    secondary: 'How it works',
    research: 'Sources and evidence levels at hand',
    profiles: 'substance profiles',
    relations: 'curated pairs',
    stacks: 'research triples',
    sectionLabel: 'BG-02 / How selection works',
    sectionTitleA: 'More than a list.',
    sectionTitleB: 'A reason for every ingredient.',
    sectionLead: 'We match your goal with the available substance data, then check the interactions within each option. When the evidence is thin or missing, we say so.',
    steps: [
      ['Goal', 'Tell us what you want to improve and what you would rather avoid.'],
      ['Matches', 'We find substances linked to the effects that matter to you.'],
      ['Checks', 'We check how the substances work together and flag potential risks.'],
      ['Results', 'Compare the suggested combinations and choose the one that suits you best.'],
    ],
    closingLabel: 'BG-03 / Start here',
    closingTitle: 'Build a combination around your goal.',
    closingCopy: 'Start with your goal. You can change the ingredients and run the check again at any time.',
    open: 'Start building',
    disclaimer: 'For education and research. Not medical advice.',
    systemRail: ['BIOHACK AI / KNOWLEDGE BASE', '97 SUBSTANCES / 29 PAIRS / 39 TRIPLES', 'FINAL PRODUCTION / 18 AUG 2026'],
    objectLabel: ['FIG. 01 / NEUROBIOLOGICAL MODEL', 'PATHWAYS / DIAGRAM'],
    footerMeta: 'MINSK / BELARUS',
  },
} as const

export function BrandGatePage() {
  const gateRef = useRef<HTMLDivElement>(null)
  const { language, setLanguage } = useLanguage()
  const text = copy[language]

  useEffect(() => {
    const gate = gateRef.current
    if (!gate) return

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    const finePointer = window.matchMedia('(pointer: fine)')
    const spectralLayer = gate.querySelector<HTMLElement>('.bg-brain-spectral')
    const pointer = {
      targetX: window.innerWidth * 0.73,
      targetY: window.innerHeight * 0.42,
      currentX: window.innerWidth * 0.73,
      currentY: window.innerHeight * 0.42,
    }
    let frame = 0

    const onPointerMove = (event: PointerEvent) => {
      pointer.targetX = event.clientX
      pointer.targetY = event.clientY
    }

    const updatePointer = () => {
      const ease = reducedMotion.matches ? 1 : 0.085
      pointer.currentX += (pointer.targetX - pointer.currentX) * ease
      pointer.currentY += (pointer.targetY - pointer.currentY) * ease

      if (!reducedMotion.matches) {
        const x = (pointer.currentX / window.innerWidth - 0.5) * 22
        const y = (pointer.currentY / window.innerHeight - 0.5) * 14
        gate.style.setProperty('--bg-parallax-x', `${x}px`)
        gate.style.setProperty('--bg-parallax-y', `${y}px`)
      }

      gate.style.setProperty('--bg-spot-x', `${pointer.currentX}px`)
      gate.style.setProperty('--bg-spot-y', `${pointer.currentY}px`)
      if (spectralLayer) {
        const bounds = spectralLayer.getBoundingClientRect()
        if (bounds.width && bounds.height) {
          const localX = (pointer.currentX - bounds.left) * spectralLayer.offsetWidth / bounds.width
          const localY = (pointer.currentY - bounds.top) * spectralLayer.offsetHeight / bounds.height
          gate.style.setProperty('--bg-spot-local-x', `${localX}px`)
          gate.style.setProperty('--bg-spot-local-y', `${localY}px`)
        }
      }

      if (finePointer.matches) {
        const cursor = gate.querySelector<HTMLElement>('.bg-cursor')
        if (cursor) cursor.style.transform = `translate3d(${pointer.currentX - 23}px, ${pointer.currentY - 23}px, 0)`
      }
      frame = window.requestAnimationFrame(updatePointer)
    }

    const hoverTargets = [...gate.querySelectorAll<HTMLElement>('a, button, .bg-step')]
    const onEnter = () => gate.classList.add('is-cursor-active')
    const onLeave = () => gate.classList.remove('is-cursor-active')
    hoverTargets.forEach((target) => {
      target.addEventListener('pointerenter', onEnter)
      target.addEventListener('pointerleave', onLeave)
    })

    const magneticTargets = [...gate.querySelectorAll<HTMLElement>('[data-magnetic]')]
    const magneticHandlers = magneticTargets.map((target) => {
      const move = (event: PointerEvent) => {
        if (!finePointer.matches || reducedMotion.matches) return
        const bounds = target.getBoundingClientRect()
        const x = (event.clientX - bounds.left - bounds.width / 2) * 0.12
        const y = (event.clientY - bounds.top - bounds.height / 2) * 0.12
        target.style.transform = `translate3d(${x}px, ${y}px, 0)`
      }
      const reset = () => { target.style.transform = 'translate3d(0, 0, 0)' }
      target.addEventListener('pointermove', move)
      target.addEventListener('pointerleave', reset)
      return { target, move, reset }
    })

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        entry.target.classList.add('is-visible')
        observer.unobserve(entry.target)
      })
    }, { threshold: 0.16, rootMargin: '0px 0px -6%' })
    gate.querySelectorAll('[data-reveal]').forEach((element) => observer.observe(element))

    window.addEventListener('pointermove', onPointerMove, { passive: true })
    frame = window.requestAnimationFrame(updatePointer)

    return () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.cancelAnimationFrame(frame)
      observer.disconnect()
      hoverTargets.forEach((target) => {
        target.removeEventListener('pointerenter', onEnter)
        target.removeEventListener('pointerleave', onLeave)
      })
      magneticHandlers.forEach(({ target, move, reset }) => {
        target.removeEventListener('pointermove', move)
        target.removeEventListener('pointerleave', reset)
      })
    }
  }, [language])

  return (
    <div className="bg-gate" ref={gateRef}>
      <div className="bg-cursor" aria-hidden="true"><span /></div>

      <header className="bg-header">
        <a className="bg-brand" href="#top" aria-label="Biohack AI">
          <span className="bg-brand-mark" aria-hidden="true" />
          <span>BIOHACK <b>AI</b></span>
        </a>
        <nav className="bg-nav" aria-label={language === 'ru' ? 'Навигация' : 'Navigation'}>
          <a href="#top">{text.navSystem}</a>
          <a href="#logic">{text.navLogic}</a>
        </nav>
        <div className="bg-header-actions">
          <div className="bg-language" role="group" aria-label={language === 'ru' ? 'Язык' : 'Language'}>
            <button className={language === 'ru' ? 'active' : ''} onClick={() => setLanguage('ru')} aria-pressed={language === 'ru'}>RU</button>
            <button className={language === 'en' ? 'active' : ''} onClick={() => setLanguage('en')} aria-pressed={language === 'en'}>EN</button>
          </div>
          <Link className="bg-header-enter" to="/app">{text.enter}<ArrowUpRight size={15} /></Link>
        </div>
      </header>

      <main>
        <section className="bg-hero" id="top">
          <div className="bg-hero-media" aria-hidden="true">
            <div className="bg-brain bg-brain-base" />
            <div className="bg-brain bg-brain-spectral" />
            <div className="bg-technical-grid" />
            <div className="bg-hero-veil" />
          </div>
          <div className="bg-system-rail" aria-hidden="true">
            {text.systemRail.map((item) => <span key={item}>{item}</span>)}
          </div>
          <div className="bg-hero-copy">
            <p className="bg-kicker bg-load-in"><span>01</span>{text.label}</p>
            <h1 className={`bg-load-in bg-load-delay-1${language === 'en' ? ' bg-hero-title-en' : ''}`}>{text.headline}</h1>
            <p className="bg-hero-lead bg-load-in bg-load-delay-2">{text.lead}</p>
            <div className="bg-hero-actions bg-load-in bg-load-delay-3">
              <Link className="bg-primary" to="/app" data-magnetic>{text.primary}<ArrowUpRight size={18} /></Link>
              <a className="bg-secondary" href="#logic">{text.secondary}<ArrowDown size={16} /></a>
            </div>
          </div>
          <div className="bg-object-label" aria-hidden="true"><span>{text.objectLabel[0]}</span><i /><span>{text.objectLabel[1]}</span></div>
          <div className="bg-hero-foot">
            <div className="bg-research-state"><i />{text.research}</div>
            <div className="bg-metrics">
              <div><strong>97</strong><span>{text.profiles}</span></div>
              <div><strong>29</strong><span>{text.relations}</span></div>
              <div><strong>39</strong><span>{text.stacks}</span></div>
            </div>
          </div>
        </section>

        <section className="bg-logic" id="logic">
          <div className="bg-logic-head" data-reveal>
            <p className="bg-section-code">{text.sectionLabel}</p>
            <h2><span>{text.sectionTitleA}</span><span>{text.sectionTitleB}</span></h2>
            <p>{text.sectionLead}</p>
          </div>
          <div className="bg-steps">
            {text.steps.map(([title, description], index) => (
              <div className="bg-step" key={title} data-reveal>
                <span className="bg-step-index">0{index + 1}</span>
                <h3>{title}</h3>
                <p>{description}</p>
                <span className="bg-step-glyph" aria-hidden="true"><span>→</span></span>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-closing">
          <div className="bg-closing-copy">
            <p className="bg-section-code">{text.closingLabel}</p>
            <h2>{text.closingTitle}</h2>
            <p>{text.closingCopy}</p>
          </div>
          <Link className="bg-closing-link" to="/app" data-magnetic><span>{text.open}</span><ArrowUpRight size={30} /></Link>
          <div className="bg-closing-foot"><span>© 2026 BIOHACK AI</span><span>{text.disclaimer}</span><span>{text.footerMeta}</span></div>
        </section>
      </main>
    </div>
  )
}
